import * as Tabs from "@radix-ui/react-tabs";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  BrushCleaning,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Funnel,
  LayoutGrid,
  List,
  Search,
  Table2,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { rangoVisible, TAMANOS_DE_PAGINA, totalPaginas } from "../../lib/paginacion";
import { Boton, Modal } from "./primitivos";

/**
 * Patrones de pantalla porteados de `imprenta` (SPEC 0003, fase 1).
 *
 * `imprenta` tiene 34 archivos en `components/ui/`, pero 27 son shadcn y **7 son
 * abstracciones propias**: `DataTable`, `FilterBar`, `StatsCards`, `ViewToggle`,
 * `PageHeader`, `StatusDot` y `confirm-modal`. Esas 7 son el esqueleto que
 * repiten todas sus pantallas, y son las que se portean acá.
 *
 * Van en un archivo aparte de `primitivos.tsx` porque son otra cosa: los
 * primitivos envuelven Radix y resuelven accesibilidad; esto resuelve
 * **composición de pantalla** y se apoya en ellos.
 *
 * Se portea el COMPORTAMIENTO, no el código (ADR 0021): la API es la de allá
 * para que portear cada pantalla sea mecánico, pero por dentro usa nuestros
 * primitivos y el tema oscuro.
 */

/**
 * El contenedor de `imprenta`: `rounded-xl border shadow` sobre blanco.
 *
 * Todo lo demas de este archivo se apoya en esto — las tarjetas, la barra de
 * filtros y la tabla son Cards. Copiarlo una vez evita que cada patron invente
 * su propio borde y su propia sombra.
 */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("rounded-xl border border-neutral-200 bg-white shadow-sm", className)}>
      {children}
    </div>
  );
}

// ─── PageHeader ────────────────────────────────────────────────────────────

/**
 * Encabezado de pantalla con acciones a la derecha.
 *
 * Se suma a `Encabezado` de `primitivos.tsx` en vez de reemplazarlo: aquel es
 * título y subtítulo, este agrega la zona de acciones y el volver, que es lo que
 * las pantallas de `imprenta` usan.
 */
export function PageHeader({
  titulo,
  subtitulo,
  acciones,
  alVolver,
  children,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  /** Si viene, se muestra el botón de volver. */
  alVolver?: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {alVolver ? (
            <button
              type="button"
              onClick={alVolver}
              aria-label="Volver"
              className="mt-1 rounded-md p-1 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          ) : null}
          <div>
            <h2 className="font-bold text-2xl text-neutral-900 tracking-tight md:text-3xl">
              {titulo}
            </h2>
            {subtitulo ? (
              <p className="mt-1 text-neutral-500 text-sm md:text-base">{subtitulo}</p>
            ) : null}
          </div>
        </div>
        {acciones ? <div className="flex shrink-0 gap-2">{acciones}</div> : null}
      </div>
      {children}
    </div>
  );
}

// ─── StatusDot ─────────────────────────────────────────────────────────────

export type EstadoPunto =
  | "ok"
  | "error"
  | "aviso"
  | "info"
  | "pendiente"
  | "borrador"
  | "imprimiendo"
  | "completado"
  | "entregado";

// La paleta `status` de `imprenta`, copiada (ADR 0027). Son SUS siete colores
// de estado, no una aproximacion con la escala de Tailwind.
const COLOR_PUNTO: Record<EstadoPunto, string> = {
  ok: "bg-status-approved",
  error: "bg-status-rejected",
  aviso: "bg-status-pending",
  info: "bg-status-printing",
  pendiente: "bg-status-pending",
  borrador: "bg-status-draft",
  imprimiendo: "bg-status-printing",
  completado: "bg-status-completed",
  entregado: "bg-status-delivered",
};

/**
 * Punto de color para señalar estado en una fila.
 *
 * **Nunca va solo**: el color no es información para quien no lo distingue. Se
 * usa al lado del texto del estado, como refuerzo.
 */
/**
 * La misma paleta, como borde izquierdo.
 *
 * Va escrita clase por clase y no compuesta con un template: Tailwind escanea
 * el fuente en busca de nombres de clase literales, así que un
 * `` `border-l-${token}` `` no genera ninguna regla y el borde sale transparente.
 */
export const BORDE_ESTADO: Record<EstadoPunto, string> = {
  ok: "border-l-status-approved",
  error: "border-l-status-rejected",
  aviso: "border-l-status-pending",
  info: "border-l-status-printing",
  pendiente: "border-l-status-pending",
  borrador: "border-l-status-draft",
  imprimiendo: "border-l-status-printing",
  completado: "border-l-status-completed",
  entregado: "border-l-status-delivered",
};

/** La paleta como fondo — para una barra, donde el punto queda chico. */
export const FONDO_ESTADO: Record<EstadoPunto, string> = COLOR_PUNTO;

export function StatusDot({ estado, className }: { estado: EstadoPunto; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block h-2 w-2 rounded-full", COLOR_PUNTO[estado], className)}
    />
  );
}

// ─── StatsCards ────────────────────────────────────────────────────────────

export interface Estadistica {
  titulo: string;
  valor: string | number;
  descripcion?: string;
  icono?: LucideIcon;
  /**
   * Variación contra el período anterior, en la misma línea que la
   * descripción — como `StatsCards` de `imprenta`.
   */
  tendencia?: { porcentaje: number; mejora: boolean };
  /**
   * El peso de lo que cuenta.
   *
   * `alerta` es para lo que está roto y hay que arreglar hoy —una combinación
   * sin precio no se puede cotizar—; `aviso` para lo que se va a romper. El
   * resto va neutro: si todo grita, nada grita.
   */
  tono?: "neutral" | "alerta" | "aviso";
  /** Si viene, la tarjeta es clickeable y filtra el listado. */
  alClickear?: () => void;
  activa?: boolean;
  /**
   * Desglose que la tarjeta muestra al pasar el mouse, cada uno filtrable.
   *
   * Es el comportamiento de `BoardStats` de `imprenta` (ADR 0063): el grupo
   * «Pendientes» se abre en sus estados y se puede filtrar por uno solo, sin
   * gastar una fila de filtros en algo que se usa de a ratos.
   */
  subEstados?: SubEstado[];
}

export interface SubEstado {
  clave: string;
  titulo: string;
  valor: number;
  activa: boolean;
  alClickear: () => void;
}

/**
 * Tarjetas de resumen que **además filtran**.
 *
 * Es la parte que se pierde si se leen como decoración: en el tablero de
 * `imprenta` clickear "En Producción" filtra la tabla. Por eso `alClickear` y
 * `activa` están en la API desde el principio.
 */
export function StatsCards({ estadisticas }: { estadisticas: Estadistica[] }) {
  return (
    // `flex flex-wrap` y no grid: es lo de `imprenta`, y hace que 3 o 5
    // tarjetas se acomoden solas en vez de dejar un hueco.
    <div className="flex flex-wrap gap-4">
      {estadisticas.map((e) => (
        <TarjetaEstadistica key={e.titulo} estadistica={e} />
      ))}
    </div>
  );
}

function TarjetaEstadistica({ estadistica: e }: { estadistica: Estadistica }) {
  const hayDesglose = Boolean(e.subEstados && e.subEstados.length > 0);
  // Filtrando por uno de sus estados, el desglose queda **fijo**: esconder el
  // control que gobierna lo que se ve deja la pantalla filtrada sin nada que lo
  // explique.
  const fijo = hayDesglose && (e.subEstados?.some((s) => s.activa) ?? false);

  return (
    // El hover va por CSS y no por estado de React: dos handlers de mouse en un
    // `div` son un elemento estático que reacciona, y lo que hace falta acá es
    // exactamente lo que `group-hover` resuelve sin JS. El desglose es hijo de
    // este contenedor, así que el hover se sostiene al moverse hacia él.
    <div className="group relative min-w-[calc(50%-8px)] flex-1 md:min-w-[120px]">
      {[e].map((e) => {
        const Icono = e.icono;
        // El tono tine el NUMERO, no la tarjeta entera: un bloque rojo de 200px
        // grita mas fuerte que el problema, y con dos al lado la pantalla se
        // vuelve una alarma. El borde se tine apenas, para que se encuentre.
        const tono = e.valor === 0 ? "neutral" : (e.tono ?? "neutral");
        const contenido = (
          <>
            {/* `p-4` y no el `p-6` de `imprenta` (ADR 0091): allá el título va
                con `leading-none` y acá con el interlineado normal, así que el
                mismo padding rinde una tarjeta más alta. Son cinco números de
                una línea — el aire de más no agrega nada y empuja la tabla,
                que es lo que se vino a mirar. */}
            <div className="flex flex-row items-start justify-between space-y-0 p-4 pb-2">
              <span className="font-medium text-neutral-500 text-sm">{e.titulo}</span>
              {/* El embudo dice que la tarjeta FILTRA, y se muestra siempre —
                  no al pasar el mouse. La capacidad que solo se descubre
                  clickeando a ver que pasa es, para el que la usa, una
                  capacidad que no esta (ADR 0088, misma regla que el `↕` de las
                  columnas ordenables).

                  Activa, el embudo pasa a `✕`: ahi lo que hace falta saber no
                  es que filtra, sino como dejar de filtrar.

                  La tarjeta que NO filtra no lleva nada, y esa ausencia es la
                  que ensena la regla: si todas tuvieran embudo, el embudo no
                  diria nada. */}
              <span className="flex shrink-0 items-center gap-1.5">
                {Icono ? <Icono className="h-4 w-4 text-neutral-500" /> : null}
                {e.alClickear ? (
                  e.activa ? (
                    <>
                      <X aria-hidden className="h-3.5 w-3.5 text-brand-600" />
                      <span className="sr-only">, filtrando — tocá para quitar el filtro</span>
                    </>
                  ) : (
                    <>
                      <Funnel aria-hidden className="h-3.5 w-3.5 text-neutral-300" />
                      <span className="sr-only">, filtra el listado</span>
                    </>
                  )
                ) : null}
              </span>
            </div>
            <div className="p-4 pt-0">
              <div
                className={cn(
                  "font-bold text-2xl",
                  tono === "alerta"
                    ? "text-red-700"
                    : tono === "aviso"
                      ? "text-amber-700"
                      : "text-neutral-900",
                )}
              >
                {e.valor}
              </div>
              {e.descripcion || e.tendencia ? (
                <div className="mt-1 text-neutral-500 text-xs">
                  {e.tendencia ? (
                    <span
                      className={cn(
                        "mr-1 font-medium",
                        e.tendencia.mejora ? "text-emerald-700" : "text-red-700",
                      )}
                    >
                      {/* El signo va en el TEXTO y no solo en el color: verde y
                          rojo no son información para quien no los distingue.
                          Allá el negativo sale sin signo, que se lee como una
                          suba. */}
                      {e.tendencia.mejora ? "+" : "−"}
                      {e.tendencia.porcentaje}%
                    </span>
                  ) : null}
                  {e.descripcion}
                </div>
              ) : null}
            </div>
          </>
        );

        const clases = cn(
          // `h-full` no es cosmética: en `imprenta` la tarjeta ES el item flex,
          // así que `align-items: stretch` las deja a todas del alto de la más
          // alta. Acá hay un `div` envolviéndola —lo pide el `group` del
          // desglose— y ese div es el que estira, dejando la tarjeta con el
          // alto de su contenido. Basta con que un título o una descripción
          // ocupe dos renglones para que esa tarjeta quede más alta que la de
          // al lado, y eso pasa en cualquier ancho, no en uno raro.
          "h-full w-full max-w-full rounded-xl border bg-white text-left shadow-sm transition-colors",
          // La tarjeta activa se marca con el color de marca, igual que el item
          // del sidebar: es el mismo lenguaje de "esto esta seleccionado".
          e.activa
            ? "border-brand-300 bg-brand-50"
            : tono === "alerta"
              ? "border-red-200"
              : tono === "aviso"
                ? "border-amber-200"
                : "border-neutral-200",
          e.alClickear && !e.activa && "hover:border-neutral-300 hover:bg-neutral-50",
        );

        // `button` solo cuando hace algo: una tarjeta decorativa como boton
        // aparece en la navegacion por teclado y no lleva a ningun lado.
        return e.alClickear ? (
          <button key={e.titulo} type="button" onClick={e.alClickear} className={clases}>
            {contenido}
          </button>
        ) : (
          <div key={e.titulo} className={clases}>
            {contenido}
          </div>
        );
      })}

      {/* El desglose flota SOBRE el contenido: empujar la tabla hacia abajo al
          pasar el mouse haría saltar toda la pantalla. */}
      {hayDesglose ? (
        <div
          className={cn(
            "absolute top-full right-0 left-0 z-30 flex-wrap justify-center gap-1 pt-1",
            fijo ? "flex" : "hidden group-hover:flex",
          )}
        >
          {e.subEstados?.map((s) => (
            <button
              key={s.clave}
              type="button"
              onClick={s.alClickear}
              className={cn(
                "rounded-md border px-2 py-1 text-xs shadow-sm transition-colors",
                s.activa
                  ? "border-brand-300 bg-brand-50 text-brand-700"
                  : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50",
              )}
            >
              {s.titulo} <span className="font-semibold">{s.valor}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── TarjetasDeEstado ──────────────────────────────────────────────────────

/**
 * La OTRA familia de tarjeta de `imprenta` (ADR 0094).
 *
 * `imprenta` tiene **dos**, y cuál usa no es arbitrario:
 *
 * | Familia | Cómo se ve | Dónde |
 * |---|---|---|
 * | **Estado** (esta) | fondo de color, **número arriba**, rótulo abajo teñido, borde de color al estar activa | Producción, Consultas, Impresoras, Usuarios |
 * | **Métrica** (`StatsCards`) | tarjeta blanca, rótulo + ícono arriba, número, descripción | Clientes, Lista de Precios, Inicio, Reportes |
 *
 * La diferencia no es decorativa: **estas SON el filtro**. El color no adorna,
 * dice de qué estado se está hablando y es el mismo del chip de la fila, así que
 * la tarjeta y la tabla se leen con el mismo código.
 *
 * Y por eso **acá no va el embudo del ADR 0090**: ahí el embudo existe porque
 * conviven tarjetas que filtran con una que no. Cuando **todas** filtran, el
 * embudo no distingue nada — el fondo de color ya dice que esto se toca.
 *
 * El número va arriba porque es lo que se viene a mirar: en una franja de
 * cinco, el ojo barre la fila de números y solo baja al rótulo del que le
 * interesa.
 */
export type TonoEstado = "neutro" | "verde" | "ambar" | "azul" | "rojo" | "gris";

/**
 * Escrito clase por clase y no compuesto con un template.
 *
 * Tailwind escanea el fuente buscando nombres de clase **literales**: un
 * `` `bg-${tono}-50` `` no genera ninguna regla y la tarjeta sale transparente,
 * **sin ningún error**. Es la misma razón por la que `BORDE_ESTADO` está escrito
 * así más arriba.
 */
const ESTILO_ESTADO: Record<TonoEstado, { fondo: string; texto: string; borde: string }> = {
  neutro: { fondo: "bg-white", texto: "text-neutral-600", borde: "border-neutral-400" },
  verde: { fondo: "bg-green-50", texto: "text-green-700", borde: "border-green-400" },
  ambar: { fondo: "bg-amber-50", texto: "text-amber-700", borde: "border-amber-400" },
  azul: { fondo: "bg-blue-50", texto: "text-blue-700", borde: "border-blue-400" },
  rojo: { fondo: "bg-red-50", texto: "text-red-700", borde: "border-red-400" },
  gris: { fondo: "bg-neutral-100", texto: "text-neutral-600", borde: "border-neutral-400" },
};

export interface TarjetaEstado {
  clave: string;
  titulo: string;
  valor: number | string;
  tono: TonoEstado;
  activa: boolean;
  alClickear: () => void;
  /**
   * Desglose que se abre al pasar el mouse, cada uno filtrable (ADR 0063).
   *
   * Es lo que hace `BoardStats` de `imprenta`: el grupo «Pendientes» se abre en
   * sus estados y se puede filtrar por uno solo, sin gastar una fila de filtros
   * en algo que se usa de a ratos.
   */
  subEstados?: SubEstado[];
}

/**
 * Escrito clase por clase, por lo mismo que `ESTILO_ESTADO`: Tailwind no genera
 * la regla de un `` `md:grid-cols-${n}` `` armado en tiempo de ejecución.
 */
const COLUMNAS: Record<number, string> = {
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
  6: "md:grid-cols-6",
};

export function TarjetasDeEstado({ tarjetas }: { tarjetas: TarjetaEstado[] }) {
  return (
    // `grid` y no `flex-wrap`: es lo de `imprenta`, y acá importa más que en
    // `StatsCards` — con todas del mismo ancho, la fila de números se lee de
    // corrido. En mobile van de a dos.
    //
    // Las columnas siguen a la CANTIDAD. Con un `md:grid-cols-5` fijo, una
    // pantalla de cuatro tarjetas —Producción, Usuarios— deja la quinta columna
    // vacía: 269 px de hueco a la derecha que no se ven contra el fondo claro
    // pero desalinean la franja con la tabla de abajo.
    <div className={cn("grid grid-cols-2 gap-4", COLUMNAS[tarjetas.length] ?? "md:grid-cols-5")}>
      {tarjetas.map((t) => {
        const estilo = ESTILO_ESTADO[t.tono];
        const hayDesglose = Boolean(t.subEstados && t.subEstados.length > 0);
        // Filtrando por uno de sus estados el desglose queda FIJO: esconder el
        // control que gobierna lo que se ve deja la pantalla filtrada sin nada
        // que lo explique.
        const fijo = hayDesglose && (t.subEstados?.some((s) => s.activa) ?? false);
        return (
          <div key={t.clave} className="group relative">
            <button
              type="button"
              aria-pressed={t.activa}
              onClick={t.alClickear}
              className={cn(
                "w-full rounded-lg border p-4 text-left transition-colors",
                estilo.fondo,
                t.activa ? estilo.borde : "border-neutral-200 hover:border-neutral-300",
              )}
            >
              <div className="font-bold text-2xl text-neutral-900">{t.valor}</div>
              <div className={cn("text-sm", estilo.texto)}>{t.titulo}</div>
              {/* `aria-pressed` dice si está activa, pero no que al tocarla se
                  filtra. Sin esto, un lector de pantalla anuncia «botón, 47,
                  Pendientes» y no hay forma de saber qué hace. */}
              <span className="sr-only">
                {t.activa ? ", filtrando — tocá para quitar el filtro" : ", filtra el listado"}
              </span>
            </button>

            {/* El desglose flota SOBRE el contenido: empujarlo hacia abajo al
                pasar el mouse haría saltar toda la pantalla. */}
            {hayDesglose ? (
              <div
                className={cn(
                  "absolute top-full right-0 left-0 z-30 flex-wrap justify-center gap-1 pt-1",
                  fijo ? "flex" : "hidden group-hover:flex",
                )}
              >
                {t.subEstados?.map((s) => (
                  <button
                    key={s.clave}
                    type="button"
                    onClick={s.alClickear}
                    className={cn(
                      "rounded-md border px-2 py-1 text-xs shadow-sm transition-colors",
                      s.activa
                        ? "border-brand-300 bg-brand-50 text-brand-700"
                        : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50",
                    )}
                  >
                    {s.titulo} <span className="font-semibold">{s.valor}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ─── ViewToggle ────────────────────────────────────────────────────────────

export type Vista = "tabla" | "tarjetas" | "lista" | "kanban";

// Un icono por vista. Los tres compartian `LayoutGrid` y con dos opciones no se
// notaba; el listado de presupuestos es el primero que ofrece TRES, y ahi dos
// botones identicos dejan de ser un detalle: el icono es lo unico que distingue
// a un boton que no tiene texto.
const ICONO_VISTA: Record<Vista, LucideIcon> = {
  tabla: Table2,
  tarjetas: LayoutGrid,
  lista: List,
  kanban: Columns3,
};

const NOMBRE_VISTA: Record<Vista, string> = {
  tabla: "Vista de tabla",
  tarjetas: "Vista de tarjetas",
  lista: "Vista de lista",
  kanban: "Vista kanban",
};

/**
 * Conmutador de vistas.
 *
 * `imprenta` lo tiene con dos opciones fijas (`cards`/`table`); acá recibe las
 * que le pasen, porque el tablero de producción usa cuatro (fase 3).
 */
export function ViewToggle({
  valor,
  opciones,
  alCambiar,
}: {
  valor: Vista;
  opciones: readonly Vista[];
  alCambiar: (v: Vista) => void;
}) {
  return (
    <div className="flex overflow-hidden rounded-lg border border-neutral-200">
      {opciones.map((opcion) => {
        const Icono = ICONO_VISTA[opcion];
        return (
          <button
            key={opcion}
            type="button"
            aria-pressed={valor === opcion}
            aria-label={NOMBRE_VISTA[opcion]}
            onClick={() => alCambiar(opcion)}
            className={cn(
              "flex h-9 w-9 items-center justify-center transition-colors",
              valor === opcion
                ? "bg-neutral-900 text-neutral-100"
                : "bg-white text-neutral-600 hover:text-neutral-800",
            )}
          >
            <Icono className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

// ─── FilterBar ─────────────────────────────────────────────────────────────

/**
 * Barra de filtros.
 *
 * `alwaysVisible` de `imprenta` es la búsqueda, que no se colapsa nunca; el
 * resto va en `children`.
 *
 * `filtrosActivos` **no se muestra**: decide si aparece «Limpiar». Ahí donde
 * `imprenta` pone un número es en un badge dentro del botón «Filtros» de
 * mobile, que existe porque ahí los filtros están colapsados y no se ven.
 */
export function FilterBar({
  busqueda,
  children,
  filtrosActivos = 0,
  alLimpiar,
}: {
  busqueda?: ReactNode;
  children?: ReactNode;
  filtrosActivos?: number;
  alLimpiar?: () => void;
}) {
  return (
    <Card className="relative flex flex-wrap items-center gap-3 p-4">
      {busqueda ? <div className="min-w-[16rem] flex-1">{busqueda}</div> : null}
      {children}
      {/* Limpiar es una **escoba montada sobre el borde**, arriba a la derecha.
          Dentro de la barra, un botón de texto entra en la fila de los filtros
          y se lee como uno más — que es lo contrario de lo que hace. Afuera y
          en el borde no compite con ellos, y aparece solo cuando hay algo que
          limpiar. El texto va en `title` y en `sr-only`: un ícono solo no le
          dice nada a un lector de pantalla. */}
      {filtrosActivos > 0 && alLimpiar ? (
        <button
          type="button"
          onClick={alLimpiar}
          title="Limpiar filtros"
          className="-top-3 -right-3 absolute grid size-7 place-items-center rounded-full border border-neutral-300 bg-white text-neutral-500 shadow-sm transition-colors hover:border-neutral-400 hover:text-neutral-800"
        >
          <BrushCleaning aria-hidden className="size-4" />
          <span className="sr-only">Limpiar filtros</span>
        </button>
      ) : null}
    </Card>
  );
}

/**
 * Un filtro de la barra, con el rótulo **arriba y en chico** cuando no hay nada
 * elegido: «Todos los / productos» en dos líneas.
 *
 * Es la forma de `imprenta`, y no es decoración: un `<select>` que dice solo
 * «Todos los productos» ocupa el mismo lugar que uno que dice «Camiseta
 * Basica», y con cuatro al lado no se sabe cuál es cuál sin leerlos. El rótulo
 * fijo arriba dice **qué** filtra; el valor abajo, **por qué**.
 */
export function FiltroSelect({
  etiqueta,
  sustantivo,
  valor,
  alCambiar,
  opciones,
  ancho = "10rem",
}: {
  /** La primera línea: «Todos los», «Todas las». */
  etiqueta: string;
  /** La segunda: «productos», «vigencias». */
  sustantivo: string;
  valor: string;
  alCambiar: (valor: string) => void;
  opciones: readonly { valor: string; texto: string }[];
  ancho?: string;
}) {
  const sinElegir = valor === "";
  return (
    <div className="relative" style={{ width: ancho }}>
      {sinElegir ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center leading-tight"
        >
          <span className="text-[10px] text-neutral-500">{etiqueta}</span>
          <span className="text-[13px] text-neutral-900">{sustantivo}</span>
        </div>
      ) : null}
      <select
        aria-label={`${etiqueta} ${sustantivo}`}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        className={cn(
          "h-[42px] w-full rounded-md border px-2 text-center text-sm",
          // Un filtro puesto se ve **encendido**, igual que el toggle: con
          // cinco controles en fila, saber cuál está filtrando no puede
          // depender de leerlos uno por uno.
          sinElegir
            ? "border-neutral-300 bg-white text-transparent"
            : "border-brand-600 bg-brand-600 font-medium text-white",
        )}
      >
        {/* Las opciones vuelven al fondo blanco: heredado, el desplegable
            quedaría blanco sobre azul en los navegadores que lo respetan. */}
        <option value="" className="bg-white font-normal text-neutral-900">
          {`${etiqueta} ${sustantivo}`}
        </option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor} className="bg-white font-normal text-neutral-900">
            {o.texto}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * El botón de dos líneas que enciende un filtro binario: «Productos /
 * Inactivos». Porteado de `imprenta`, que lo usa para incluir en la lista lo
 * que por defecto no está.
 */
export function FiltroToggle({
  etiqueta,
  sustantivo,
  activo,
  alCambiar,
}: {
  etiqueta: string;
  sustantivo: string;
  activo: boolean;
  alCambiar: (activo: boolean) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={() => alCambiar(!activo)}
      className={cn(
        "flex h-[42px] flex-col items-center justify-center rounded-md border px-3 leading-tight transition-colors",
        activo
          ? "border-brand-600 bg-brand-600 text-white"
          : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50",
      )}
    >
      <span className="text-[10px]">{etiqueta}</span>
      <span className="font-medium text-xs">{sustantivo}</span>
    </button>
  );
}

// ─── CampoDeBusqueda ───────────────────────────────────────────────────────

/**
 * El buscador de `imprenta`: **la lupa DENTRO del input** (ADR 0095).
 *
 * Un campo de texto sin nada que lo marque como búsqueda se lee como un campo
 * más — y en una barra de filtros, donde todo lo demás es un `select`, el que
 * es libre necesita decirlo.
 *
 * Estaba escrito así **inline en Lista de Precios**. Se extrae acá porque L9 lo
 * pide en otras tres pantallas, y cuatro copias del mismo `absolute left-3` es
 * el camino corto a que una quede con la lupa a distinta altura.
 *
 * `ancho` existe porque `imprenta` no lo pone igual en todos lados: en
 * Impresoras el campo es angosto y en Clientes ocupa la barra entera.
 */
export function CampoDeBusqueda({
  valor,
  alCambiar,
  placeholder,
  etiqueta = "Buscar",
  ancho = "completo",
}: {
  valor: string;
  alCambiar: (v: string) => void;
  placeholder: string;
  etiqueta?: string;
  ancho?: "completo" | "medio";
}) {
  return (
    <div className={cn("relative", ancho === "medio" ? "md:max-w-[450px]" : "w-full")}>
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-neutral-400"
      />
      <input
        aria-label={etiqueta}
        placeholder={placeholder}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        className="h-[42px] w-full rounded-md border border-neutral-300 bg-white py-1.5 pr-3 pl-9 text-neutral-900 text-sm"
      />
    </div>
  );
}

// ─── DataTable ─────────────────────────────────────────────────────────────

export interface Columna<T> {
  clave: string;
  encabezado: string;
  /**
   * `indice` es la posición en la lista YA renderizada. Lo necesita cualquier
   * columna que ofrezca reordenar —subir/bajar— para saber si está en un
   * extremo. `imprenta` no lo pasa y por eso su `DataTable` no sirve para
   * catálogos ordenables; es una extensión, no una divergencia.
   */
  render: (item: T, indice: number) => ReactNode;
  className?: string;
  /** Si viene, la columna es ordenable y el encabezado se vuelve botón. */
  ordenarPor?: string;
}

/**
 * Tabla con sus tres estados: cargando, vacía y con datos.
 *
 * Es la abstracción que más se repite en `imprenta`, y lo que aporta no es el
 * `<table>` sino **los estados**: hoy cada pantalla nuestra escribe su propio
 * "No hay resultados" y ninguna muestra que está cargando.
 */
export function DataTable<T>({
  datos,
  columnas,
  clave,
  cargando = false,
  mensajeVacio = "No hay datos disponibles",
  orden,
  alOrdenar,
  claseDeFila,
  alClickearFila,
}: {
  datos: readonly T[];
  columnas: readonly Columna<T>[];
  clave: (item: T) => string;
  cargando?: boolean;
  mensajeVacio?: string;
  orden?: { campo: string; direccion: "asc" | "desc" } | null;
  alOrdenar?: (campo: string) => void;
  /**
   * Clases extra para la fila, por item. `imprenta` tiñe el fondo según el
   * estado —un precio programado y uno vencido se leen igual sin eso—, y esa
   * decisión es de la pantalla, no de la tabla.
   */
  claseDeFila?: (item: T) => string;
  /**
   * Abre el item al clickear la fila entera, como las tablas de `imprenta`.
   *
   * La fila es un `<tr>` con `onClick` y **no** un `<button>`: un botón dentro
   * de `<tr>` rompe la tabla. Lleva `tabIndex` y responde a Enter, porque una
   * fila que solo obedece al mouse deja la pantalla sin camino por teclado.
   *
   * Un click sobre un control de la fila —el botón «Desactivar»— hace lo suyo y
   * **no** navega: si navegara, se perdería el resultado de la acción que se
   * acaba de disparar.
   */
  alClickearFila?: (item: T) => void;
}) {
  return (
    <Card className="overflow-hidden">
      {/* `relative`: el `sr-only` de los encabezados es `absolute`, y sin un
          ancestro posicionado acá se ubica contra la página y la ensancha. */}
      <div className="relative overflow-x-auto">
        {/* SIN `min-w-max`, al reves que `Tabla` — y la diferencia es a
            proposito. `Tabla` vive adentro de un modal, donde el ancho esta
            acotado y scrollear es la unica salida. Esta ocupa la pagina
            entera: forzarle el ancho natural empuja la columna de acciones
            fuera de la vista, y es peor que una celda que envuelve. */}
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-neutral-200 [&_tr]:border-b">
            <tr>
              {columnas.map((columna) => (
                <th
                  key={columna.clave}
                  className={cn(
                    "h-10 px-4 text-left align-middle font-medium text-neutral-500",
                    columna.className,
                  )}
                >
                  {columna.ordenarPor && alOrdenar ? (
                    <button
                      type="button"
                      onClick={() => alOrdenar(columna.ordenarPor as string)}
                      className="flex items-center gap-1 hover:text-neutral-800"
                    >
                      {columna.encabezado}
                      {/* El `↕` gris se muestra SIEMPRE que la columna sea
                          ordenable, como en `imprenta`. Sin él, que se pueda
                          ordenar solo se descubre clickeando el encabezado a
                          ver qué pasa: la capacidad existía y era invisible. */}
                      {orden?.campo === columna.ordenarPor ? (
                        <span aria-hidden>{orden.direccion === "asc" ? "↑" : "↓"}</span>
                      ) : (
                        <span aria-hidden className="text-neutral-300">
                          ↕
                        </span>
                      )}
                      <span className="sr-only">
                        {orden?.campo === columna.ordenarPor
                          ? `, ordenado ${orden.direccion === "asc" ? "ascendente" : "descendente"}`
                          : ", ordenable"}
                      </span>
                    </button>
                  ) : (
                    columna.encabezado
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {cargando ? (
              <tr>
                <td colSpan={columnas.length} className="px-4 py-8 text-center text-neutral-500">
                  Cargando...
                </td>
              </tr>
            ) : datos.length === 0 ? (
              <tr>
                <td colSpan={columnas.length} className="px-4 py-8 text-center text-neutral-500">
                  {mensajeVacio}
                </td>
              </tr>
            ) : (
              datos.map((item, indice) => (
                <tr
                  key={clave(item)}
                  tabIndex={alClickearFila ? 0 : undefined}
                  onClick={
                    alClickearFila
                      ? (e) => {
                          // El botón de la fila hace lo suyo y nada más. Se
                          // mira el target en vez de cortar la propagación en
                          // cada celda: así vale para cualquier control, y no
                          // hay que acordarse de marcar la columna.
                          if ((e.target as HTMLElement).closest("button, a, input, select")) return;
                          alClickearFila(item);
                        }
                      : undefined
                  }
                  onKeyDown={
                    alClickearFila
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          alClickearFila(item);
                        }
                      : undefined
                  }
                  className={cn(
                    "border-neutral-200 border-b transition-colors hover:bg-neutral-50",
                    alClickearFila && "cursor-pointer",
                    claseDeFila?.(item),
                  )}
                >
                  {columnas.map((columna) => (
                    <td key={columna.clave} className={cn("px-4 py-2", columna.className)}>
                      {columna.render(item, indice)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Paginacion ────────────────────────────────────────────────────────────

/**
 * El paginador de las tablas de `imprenta` (ADR 0078).
 *
 * Tiene tres partes y ninguna sobra: el **selector de tamaño**, porque diez
 * filas alcanzan para mirar y cincuenta para buscar; el **«de N registros»**,
 * que es lo único que dice cuánto hay más allá de lo que se ve; y los **cuatro
 * botones**, con «primera» y «última» — sin ellos, llegar al final de un
 * listado de treinta páginas es clickear treinta veces.
 *
 * No decide nada: recibe la página ya recortada por `paginaValida`. Si mostrara
 * una página que no existe, el error sería una tabla vacía con el paginador
 * diciendo que hay filas.
 */
export function Paginacion({
  pagina,
  tamano,
  total,
  alCambiarPagina,
  alCambiarTamano,
}: {
  pagina: number;
  tamano: number;
  total: number;
  alCambiarPagina: (pagina: number) => void;
  /** Si no viene, el tamaño es fijo y el selector no se muestra. */
  alCambiarTamano?: (tamano: number) => void;
}) {
  const paginas = totalPaginas(total, tamano);
  const { desde, hasta } = rangoVisible(pagina, tamano, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-neutral-600 text-sm">
      <div className="flex items-center gap-2">
        {alCambiarTamano ? (
          <>
            <span>Mostrando</span>
            <select
              aria-label="Filas por página"
              value={tamano}
              onChange={(e) => alCambiarTamano(Number(e.target.value))}
              className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-neutral-900 text-sm"
            >
              {TAMANOS_DE_PAGINA.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <span>de {total} registros</span>
          </>
        ) : (
          <span>
            Mostrando {desde}–{hasta} de {total}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <span className="mr-2">
          Página {pagina} de {paginas}
        </span>
        <Boton
          variante="secundario"
          className="px-2"
          aria-label="Primera página"
          disabled={pagina === 1}
          onClick={() => alCambiarPagina(1)}
        >
          <ChevronsLeft aria-hidden="true" className="h-4 w-4" />
        </Boton>
        <Boton
          variante="secundario"
          className="px-2"
          aria-label="Página anterior"
          disabled={pagina === 1}
          onClick={() => alCambiarPagina(pagina - 1)}
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </Boton>
        <Boton
          variante="secundario"
          className="px-2"
          aria-label="Página siguiente"
          disabled={pagina >= paginas}
          onClick={() => alCambiarPagina(pagina + 1)}
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </Boton>
        <Boton
          variante="secundario"
          className="px-2"
          aria-label="Última página"
          disabled={pagina >= paginas}
          onClick={() => alCambiarPagina(paginas)}
        >
          <ChevronsRight aria-hidden="true" className="h-4 w-4" />
        </Boton>
      </div>
    </div>
  );
}

// ─── ConfirmModal ──────────────────────────────────────────────────────────

/**
 * Confirmación antes de algo irreversible.
 *
 * Hoy las pantallas usan `window.confirm`, que no se puede estilar, bloquea el
 * hilo y no distingue una baja lógica de un borrado real. `variante` existe para
 * eso: lo destructivo se ve destructivo.
 */
export function ConfirmModal({
  abierto,
  onCerrar,
  onConfirmar,
  titulo,
  descripcion,
  textoConfirmar = "Confirmar",
  textoCancelar = "Cancelar",
  variante = "normal",
}: {
  abierto: boolean;
  onCerrar: () => void;
  onConfirmar: () => void;
  titulo: string;
  descripcion: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  variante?: "normal" | "destructiva";
}) {
  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo={titulo} descripcion={descripcion}>
      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          {textoCancelar}
        </Boton>
        <Boton
          variante={variante === "destructiva" ? "peligro" : "primario"}
          onClick={() => {
            onConfirmar();
            onCerrar();
          }}
        >
          {textoConfirmar}
        </Boton>
      </div>
    </Modal>
  );
}

// ─── Pestañas ──────────────────────────────────────────────────────────────

/**
 * Pestañas con contador, porteadas de las de `ProductoDetailPage` (ADR 0065).
 *
 * El contador en la etiqueta —«Precios (12)»— es de allá y hace trabajo: dice
 * si vale la pena entrar antes de entrar. Sin él, la única forma de saber que
 * un producto no tiene precios cargados es abrir la pestaña.
 *
 * Radix pone lo que no se ve y es lo que cuesta: `role="tablist"`, el foco que
 * viaja con las flechas y el `aria-controls` que ata cada pestaña con su panel.
 */
export function Pestanas({
  valor,
  alCambiar,
  pestanas,
  children,
}: {
  valor: string;
  alCambiar: (valor: string) => void;
  pestanas: readonly { clave: string; texto: string; cantidad?: number; icono?: ReactNode }[];
  children: ReactNode;
}) {
  return (
    <Tabs.Root value={valor} onValueChange={alCambiar}>
      <Tabs.List className="mb-4 flex gap-1 rounded-lg bg-neutral-100 p-1">
        {pestanas.map((p) => (
          <Tabs.Trigger
            key={p.clave}
            value={p.clave}
            className={cn(
              "flex items-center gap-2 rounded-md px-6 py-2.5 font-semibold text-sm transition-all",
              "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900",
              "data-[state=active]:border data-[state=active]:border-brand-200 data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm",
            )}
          >
            {p.icono}
            {p.texto}
            {p.cantidad === undefined ? null : <span>({p.cantidad})</span>}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {children}
    </Tabs.Root>
  );
}

/** El contenido de una pestaña. Va dentro de `Pestanas`. */
export function Pestana({ clave, children }: { clave: string; children: ReactNode }) {
  return <Tabs.Content value={clave}>{children}</Tabs.Content>;
}
